/**
 * Owns the map-related state in MPV: the active basemap style, the
 * padding around the map, the latest camera options (captured on every
 * pan-end), the mapbox-gl `Map` instance once it mounts, and the
 * imperative MapHandle ref. Also runs the camera-tracking listener and
 * the basemap-style-on-feature-count-threshold effect. Composing
 * effects (initial fit, focused-note fit) stay in view.tsx because they
 * coordinate multiple hooks.
 */

import * as I from 'immutable';
import isEqual from 'lodash/isEqual';
import React from 'react';

import {ApiOrganization, getOrgBasemapStyle} from 'app/modules/Remote/Organization';
import {usePrevious} from 'app/utils/hookUtils';
import * as mapUtils from 'app/utils/mapUtils';

interface Args {
  organization: I.ImmutableOf<ApiOrganization>;
  selectedFeatureIdParams: I.Set<string>;
}

export interface MapLifecycle {
  mapStyle: mapUtils.MapStyle;
  setMapStyle: React.Dispatch<React.SetStateAction<mapUtils.MapStyle>>;
  mapPaddingOptions: mapboxgl.PaddingOptions;
  onMapPaddingChanged: (next: mapboxgl.PaddingOptions) => void;
  /**
   * Stable callback that returns the most recently observed camera state.
   * Backed by a ref written from a `moveend` listener — does NOT trigger
   * MPV re-renders on every pan/fit/zoom. Consumers should call this
   * lazily (e.g. when opening a share modal, creating a note, mounting
   * a map) rather than reading on every render.
   */
  getLatestCameraOptions: () => mapUtils.CameraOptions | null;
  mapboxMap: mapboxgl.Map | null;
  setMapboxMap: React.Dispatch<React.SetStateAction<mapboxgl.Map | null>>;
}

export default function useMapLifecycle({
  organization,
  selectedFeatureIdParams,
}: Args): MapLifecycle {
  // 0 features right now is for the tasking tool, where we don't want to be
  // distracted by a basemap image.
  const [mapStyle, setMapStyle] = React.useState<mapUtils.MapStyle>(() =>
    selectedFeatureIdParams.size <= 1
      ? getOrgBasemapStyle(organization)
      : mapUtils.MAPBOX_SATELLITE_STREETS_STYLE
  );
  const [mapPaddingOptions, setMapPaddingOptions] = React.useState<mapboxgl.PaddingOptions>({
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  });
  // Backed by a ref so `moveend` updates don't re-render MPV. Consumers
  // call `getLatestCameraOptions()` lazily (share modal open, note
  // creation, map mount) — none of them need a render-time value.
  const latestCameraOptionsRef = React.useRef<mapUtils.CameraOptions | null>(null);
  const getLatestCameraOptions = React.useCallback(() => latestCameraOptionsRef.current, []);
  const [mapboxMap, setMapboxMap] = React.useState<mapboxgl.Map | null>(null);

  const prevFeatureCount = usePrevious(selectedFeatureIdParams.size);

  function onMapPaddingChanged(newMapPaddingOptions: mapboxgl.PaddingOptions) {
    if (!isEqual(mapPaddingOptions, newMapPaddingOptions)) {
      setMapPaddingOptions(newMapPaddingOptions);
    }
  }

  // Switch map style when feature count crosses the 1-feature threshold
  React.useEffect(() => {
    if (prevFeatureCount === undefined) return;
    if (prevFeatureCount > 1 && selectedFeatureIdParams.size <= 1) {
      setMapStyle(getOrgBasemapStyle(organization));
    } else if (prevFeatureCount <= 1 && selectedFeatureIdParams.size > 1) {
      setMapStyle(mapUtils.MAPBOX_SATELLITE_STREETS_STYLE);
    }
  }, [selectedFeatureIdParams.size, prevFeatureCount, organization]);

  // Track the camera so share-links, default cameras across mode switches, and
  // note creation can capture the user's current pitch/bearing/zoom.
  // Writes a ref (no setState) — consumers read via getLatestCameraOptions()
  // when they actually need the value.
  React.useEffect(() => {
    if (!mapboxMap) return;
    const handler = () => {
      latestCameraOptionsRef.current = {
        center: mapboxMap.getCenter(),
        pitch: mapboxMap.getPitch(),
        bearing: mapboxMap.getBearing(),
        zoom: mapboxMap.getZoom(),
      };
    };
    mapboxMap.on('moveend', handler);
    return () => {
      mapboxMap.off('moveend', handler);
    };
  }, [mapboxMap]);

  return {
    mapStyle,
    setMapStyle,
    mapPaddingOptions,
    onMapPaddingChanged,
    getLatestCameraOptions,
    mapboxMap,
    setMapboxMap,
  };
}
